import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listAccountSignals,
  markSignalReviewed,
  type AccountSignalRow,
} from "@/lib/admin.signals.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/signals")({
  component: AdminSignalsPage,
});

function AdminSignalsPage() {
  const [openOnly, setOpenOnly] = useState(true);
  const [severityMin, setSeverityMin] = useState<number>(1);

  const listFn = useServerFn(listAccountSignals);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "signals", { openOnly, severityMin }],
    queryFn: () => listFn({ data: { openOnly, severityMin, limit: 200 } }),
  });

  const grouped = useMemo(() => data?.signals ?? [], [data]);

  return (
    <div className="space-y-6">
      <div>
        <p className="overline mb-2">Integrity</p>
        <h2 className="font-serif text-2xl text-primary mb-4">Account activity signals</h2>
        <div className="flex flex-wrap gap-4 items-center mb-4 text-sm">
          <label className="flex items-center gap-2 text-ink-secondary">
            <input
              type="checkbox"
              checked={openOnly}
              onChange={(e) => setOpenOnly(e.target.checked)}
            />
            Open only
          </label>
          <label className="flex items-center gap-2 text-ink-secondary">
            Severity ≥
            <select
              value={severityMin}
              onChange={(e) => setSeverityMin(Number(e.target.value))}
              className="border border-border bg-background px-2 py-1 text-sm"
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          {data && (
            <span className="text-xs text-ink-tertiary ml-auto font-mono">
              {grouped.length} shown
            </span>
          )}
        </div>
      </div>

      {isLoading && <p className="text-sm text-ink-tertiary">Loading…</p>}
      {error && (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load signals"}
        </p>
      )}

      {data && (
        <ul className="border border-border divide-y divide-border bg-card">
          {grouped.map((s) => (
            <SignalRow key={s.id} signal={s} />
          ))}
          {grouped.length === 0 && (
            <li className="p-6 text-center text-ink-tertiary text-sm">
              No signals match the filters.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function SignalRow({ signal }: { signal: AccountSignalRow }) {
  const qc = useQueryClient();
  const reviewFn = useServerFn(markSignalReviewed);
  const [note, setNote] = useState("");
  const [expanded, setExpanded] = useState(false);

  const review = useMutation({
    mutationFn: (input: { reopen?: boolean }) =>
      reviewFn({ data: { id: signal.id, note: note || undefined, reopen: input.reopen } }),
    onSuccess: () => {
      toast.success("Signal updated");
      qc.invalidateQueries({ queryKey: ["admin", "signals"] });
      qc.invalidateQueries({ queryKey: ["admin", "user-signals"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const open = signal.reviewed_at == null;

  return (
    <li className="p-4 text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className={`inline-block px-2 py-0.5 text-xs font-mono ${
              signal.severity >= 4
                ? "bg-destructive text-destructive-foreground"
                : signal.severity >= 2
                  ? "bg-amber-200 text-amber-900"
                  : "bg-muted text-ink-secondary"
            }`}
          >
            sev {signal.severity}
          </span>
          <span className="font-mono text-xs text-primary">{signal.signal_type}</span>
          <span className="text-ink-secondary">
            {signal.user_display_name ?? signal.user_email ?? signal.user_id.slice(0, 8)}
          </span>
        </div>
        <span className="text-xs text-ink-tertiary font-mono">
          {new Date(signal.created_at).toLocaleString("en-GB")}
        </span>
      </div>

      {signal.user_email && (
        <p className="text-xs font-mono text-ink-tertiary mt-1">{signal.user_email}</p>
      )}

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-xs text-primary underline underline-offset-4 mt-2"
      >
        {expanded ? "Hide details" : "Show details"}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {signal.payload && (
            <pre className="bg-muted/50 border border-border p-2 text-[11px] font-mono overflow-x-auto">
              {signal.payload}
            </pre>
          )}
          {!open && (
            <div className="text-xs text-ink-tertiary">
              Reviewed {new Date(signal.reviewed_at!).toLocaleString("en-GB")}
              {signal.reviewer_note ? ` — ${signal.reviewer_note}` : ""}
            </div>
          )}
          {open ? (
            <div className="flex flex-col gap-2">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Reviewer note (optional)…"
                className="w-full border border-border bg-background p-2 text-xs font-mono"
              />
              <div>
                <button
                  type="button"
                  onClick={() => review.mutate({})}
                  disabled={review.isPending}
                  className="bg-primary text-primary-foreground px-3 py-1.5 text-xs disabled:opacity-50"
                >
                  {review.isPending ? "Saving…" : "Mark reviewed"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => review.mutate({ reopen: true })}
              disabled={review.isPending}
              className="border border-border px-3 py-1.5 text-xs disabled:opacity-50"
            >
              Reopen
            </button>
          )}
        </div>
      )}
    </li>
  );
}
